import { useQuery } from 'react-query'

import useAuth from 'hooks/use-auth'
import { grafanaQueryKeys } from 'queries/grafana/query-keys'
import {
  buildGrafanaRulesUrl,
  type GrafanaInstance,
  parseGrafanaRuleInstances
} from 'utils/status-checks/grafana-instance-filters'
import { testTeamExternalIntegrationEndpointConfiguration } from 'utils/team-external-integrations/test-endpoint-configuration'

import type { MutableRefObject } from 'react'
import type { TeamExternalIntegration } from 'types/api/status-checks/status-check'
import type { MultiValueTags } from 'types/tags'

/*
 * Grafana's rules API lists every instance of a rule (firing or not). It goes through
 * the generic run-test-endpoint so stored credentials and PNI are handled for us.
 */
function fetchGrafanaRuleInstances({
  integration,
  multiSelectTags,
  ruleUid,
  signal,
  statusCheckId,
  teamId
}: {
  integration: TeamExternalIntegration
  multiSelectTags?: MultiValueTags
  ruleUid: string
  signal?: AbortSignal
  statusCheckId?: string
  teamId: string
}) {
  const abortControllerRef: MutableRefObject<AbortController | undefined> = {
    current: undefined
  }
  signal?.addEventListener('abort', () => abortControllerRef.current?.abort())

  return new Promise<GrafanaInstance[] | null>((resolve, reject) => {
    void testTeamExternalIntegrationEndpointConfiguration({
      abortControllerRef,
      integration: {
        ...integration,
        url: buildGrafanaRulesUrl(integration.url ?? '', ruleUid)
      },
      multiSelectTags,
      statusCheckId,
      teamId,
      onError: (responseBody) =>
        reject(
          new Error(responseBody ?? 'Grafana rule instances fetch failed')
        ),
      onSuccess: ({ responseBody }) =>
        resolve(parseGrafanaRuleInstances(responseBody ?? '', ruleUid))
    })
  })
}

/**
 * Instances of a Grafana alert rule, or null when they can't be determined.
 */
export function useGetGrafanaRuleInstances({
  integration,
  multiSelectTags,
  ruleUid,
  statusCheckId
}: {
  integration: TeamExternalIntegration | undefined
  multiSelectTags?: MultiValueTags
  ruleUid: string | undefined
  statusCheckId?: string
}) {
  const { currentTeamId } = useAuth()

  return useQuery({
    enabled: Boolean(integration?.url && ruleUid),
    queryKey: grafanaQueryKeys.getRuleInstances({
      integrationName: integration?.name ?? '',
      multiSelectTags: multiSelectTags ?? {},
      ruleUid: ruleUid ?? '',
      teamId: currentTeamId
    }),
    queryFn: ({ signal }) =>
      integration && ruleUid
        ? fetchGrafanaRuleInstances({
            integration,
            multiSelectTags,
            ruleUid,
            signal,
            statusCheckId,
            teamId: currentTeamId
          })
        : null,
    retry: false,
    refetchOnWindowFocus: false
  })
}
